import { useState } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import type { ReportingAttributionHealth } from '../../types/reporting';
import { formatCurrencyAmounts, formatReportingAmount } from '../../lib/reportingMoney';

// §3b attribution health — the agency-level "naming gaps" panel for shared ad
// accounts. Two indicators:
//   - UNATTRIBUTED: shared-account campaigns whose name matched no location's
//     tokens (real spend, no owner) — set/fix tokens on the account form.
//   - COLLISION: campaigns whose name matched >=2 locations (flagged, never
//     guessed) — make the tokens more specific.
// Both are scoped to the date range (+ currency + campaign search), NOT the
// location filters (these rows have no location). Renders nothing when the data
// is clean (both zero), so it stays out of the way until there's a gap.
export function AttributionHealthPanel({
  health,
  isLoading,
}: {
  health: ReportingAttributionHealth | undefined;
  isLoading: boolean;
}) {
  const [showCollisions, setShowCollisions] = useState(false);

  if (!health) {
    return isLoading ? (
      <div className="mb-6 h-10 animate-pulse rounded-md bg-slate-50" aria-hidden />
    ) : null;
  }

  const { unattributed, collisions } = health;
  if (unattributed.campaigns === 0 && collisions.campaigns === 0) {
    return (
      <p className="mb-6 text-xs text-slate-400">
        attribution clean — every shared-account campaign resolves to one location.
      </p>
    );
  }

  return (
    <section className="mb-6 space-y-2">
      {unattributed.campaigns > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <HelpCircle className="h-4 w-4 shrink-0" />
          <span>
            <span className="font-semibold">{unattributed.campaigns}</span> unattributed campaign
            {unattributed.campaigns === 1 ? '' : 's'} ·{' '}
            <span className="font-semibold">{formatCurrencyAmounts(unattributed.spend)}</span> in
            shared ad accounts.
          </span>
          <span className="text-xs text-amber-700">
            Their names match no location's tokens — set/fix tokens on the account form.
          </span>
        </div>
      )}

      {collisions.campaigns > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <div className="flex flex-wrap items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <span className="font-semibold">{collisions.campaigns}</span> collision
              {collisions.campaigns === 1 ? '' : 's'} ·{' '}
              <span className="font-semibold">{formatCurrencyAmounts(collisions.spend)}</span> —
              campaigns matching 2+ locations (not attributed to any).
            </span>
            {collisions.items.length > 0 && (
              <button
                type="button"
                className="text-xs font-medium underline hover:no-underline"
                onClick={() => setShowCollisions((v) => !v)}
              >
                {showCollisions ? 'hide' : 'show'} {collisions.items.length}
              </button>
            )}
          </div>
          {showCollisions && collisions.items.length > 0 && (
            <ul className="mt-2 space-y-1 border-t border-red-200 pt-2 text-xs">
              {collisions.items.map((c, i) => (
                <li key={`${c.campaignName}-${i}`} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-red-900">{c.campaignName}</span>
                  <span className="text-red-700">
                    → {c.locations.join(' + ')} ({formatReportingAmount(c.spend.amount, c.spend.currency)})
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-xs text-red-700">
            Make the tokens more specific so only one location matches.
          </p>
        </div>
      )}
    </section>
  );
}
